import { useState } from 'react'
import { useTranslation } from '@spcc/core'
import { Badge, Button, Checkbox, Input, cn } from '@spcc/uikit'
import { Search, X } from '@spcc/uikit'
import type { SubCustomer } from '../../domain/types'
import { T_KEYS } from '../../domain/constants'
import { SubCustomerTypeBadge } from './sub-customer-type'
import { useSubCustomerOptions } from './use-sub-customer-options'

const K = T_KEYS.SUB_CUSTOMERS

export interface SubCustomerMultiSelectProps {
  /** Selected sub-customer documentIds. */
  value: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
}

function ResultRow({ customer, checked, onToggle }: { customer: SubCustomer; checked: boolean; onToggle: () => void }) {
  return (
    <label className={cn('flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-transparent p-2', checked ? 'border-primary/20 bg-primary/5' : 'hover:bg-muted/50')}>
      <Checkbox checked={checked} onCheckedChange={onToggle} className="shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{customer.name}</span>
          <SubCustomerTypeBadge type={customer.customerType} className="text-[10px]" />
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {[customer.code, customer.tenant?.name].filter(Boolean).join(' · ')}
        </span>
      </span>
    </label>
  )
}

/** Pick any number of sub-customers by searching the server (name, code or phone). Picks show as removable chips. */
export function SubCustomerMultiSelect({ value, onChange, disabled }: SubCustomerMultiSelectProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const { results, loading, known } = useSubCustomerOptions(query, value)
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])

  return (
    <div className={cn('space-y-3', disabled && 'pointer-events-none opacity-50')}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => (
            <Badge key={id} variant="secondary" className="h-8 gap-1 rounded-full ps-3 pe-1 text-xs font-normal">
              <span className="max-w-[10rem] truncate">{known[id]?.name ?? id}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" onClick={() => toggle(id)} aria-label={t(K.REMOVE)}>
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t(K.SEARCH_PLACEHOLDER)} className="h-11 ps-9 sm:h-9" />
      </div>
      <div className="max-h-60 space-y-1 overflow-y-auto">
        {loading && results.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">{t(K.SEARCHING)}</p>}
        {!loading && results.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">{t(K.NO_RESULTS)}</p>}
        {results.map((customer) => (
          <ResultRow key={customer.documentId} customer={customer} checked={value.includes(customer.documentId)} onToggle={() => toggle(customer.documentId)} />
        ))}
      </div>
    </div>
  )
}
